import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Clock, CheckCircle2, Edit3, X, XCircle, PlugZap, RefreshCw, BellRing } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost, apiPatch } from '../../hooks/useApi.js';
import { SentimentChip } from '../../components/ui/StatusBadge.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { relativeTime, formatDuration, formatDate } from '../../lib/format.js';
import type { CallSentiment } from '../../api/types.js';
import { CallsWorkspace } from './CallsWorkspace.js';
import { VerificationPanel } from './CallVerificationPanel.js';
import { NewTicketProposalCard } from './NewTicketProposalCard.js';

// ─── API contract (apps/api/src/routes/calls.ts + services/calls.service.ts) ──

interface CallDraft {
  id: string;
  status: string; // draft | pending_review | posted | rejected
  cwTicketId: number;
  notes: string;
}

interface CallListItem {
  id: string;
  direction: string;
  callerNumber: string;
  calleeNumber: string;
  startedAt: string;
  durationSec?: number | null;
  recordingUrl?: string | null;
  recordingAvailable?: boolean;
  recordingContentType?: string | null;
  recordingSizeBytes?: number | null;
  statusReason?: string | null;
  processingAttempts?: number;
  nextRetryAt?: string | null;
  cwTicketId?: number | null;
  status: string;
  callerName?: string | null;
  companyName?: string | null;
  sentiment?: number | null;
  draft?: CallDraft | null;
}

interface CallDetailDto extends CallListItem {
  mappingReason?: string | null;
  transcript?: {
    wordCount: number;
    turns: Array<{ speaker: 'tech' | 'client' | 'unknown'; text: string; startSec?: number }>;
  } | null;
  analysis?: {
    summary: Record<string, unknown>;
    sentiment1To5: number;
    reason: string;
    confidence: number;
  } | null;
  alerts: Array<{ id: string; kind: string; severity: string; message: string; acknowledged: boolean; createdAt: string }>;
}

interface CallsStatus {
  threecx: { connected: boolean; status: string; lastTestedAt: string | null; lastError: string | null };
  totalCalls: number;
  lastCallAt: string | null;
}

function asSentiment(v: number | null | undefined): CallSentiment | null {
  return v != null && v >= 1 && v <= 5 ? (Math.round(v) as CallSentiment) : null;
}

function friendlyError(error: string): string {
  if (error.includes('403')) return "Your role doesn't have access to call intelligence.";
  if (error.includes('401')) return 'Your session expired — log out and back in.';
  return `${error} — try refreshing.`;
}

function CallsPanel() {
  const navigate = useNavigate();
  const status = useGet<CallsStatus>('/api/v1/calls/status');
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const { data, loading, error, refetch } = useGet<{ items: CallListItem[]; nextCursor?: string }>(
    `/api/v1/calls?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );
  const [older, setOlder] = React.useState<CallListItem[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [syncing, setSyncing] = React.useState(false);

  // Most recent 500 calls only — the list is sorted startedAt desc, so once
  // 500 are loaded we stop paging no matter how far the CDR history goes.
  const MAX_CALLS = 500;

  const calls = React.useMemo(() => {
    const seen = new Set<string>();
    return [...older, ...(data?.items ?? [])]
      .filter(c => (seen.has(c.id) ? false : (seen.add(c.id), true)))
      .slice(0, MAX_CALLS);
  }, [older, data]);

  const atCap = calls.length >= MAX_CALLS;

  function loadMore() {
    if (!data?.nextCursor || atCap) return;
    setOlder(calls);
    setCursor(data.nextCursor);
  }

  function refreshAll() {
    setOlder([]);
    setCursor(undefined);
    refetch();
    status.refetch();
  }

  async function syncNow() {
    setSyncing(true);
    try { await apiPost("/api/v1/calls/sync", {}); refreshAll(); } finally { setSyncing(false); }
  }

  // On wide screens the list and the detail sit side by side, so open the first call
  // instead of showing an empty pane. On phones the detail replaces the list, so we leave it closed.
  React.useEffect(() => {
    if (selectedId || calls.length === 0) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches) {
      setSelectedId(calls[0]?.id ?? null);
    }
  }, [calls, selectedId]);

  const threecx = status.data?.threecx;
  const notConnected = !!threecx && !threecx.connected;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Call list */}
      <div className={cn('flex flex-col border-r border-[hsl(var(--border))]', selectedId ? 'hidden md:flex md:w-80 xl:w-96' : 'flex-1')}>
        <div className="px-4 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-start gap-2">
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[hsl(var(--content))]">Calls</h1>
            <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
              {loading && calls.length === 0 ? 'Loading…' : `${status.data?.totalCalls ?? calls.length} calls ingested`}
              {status.data?.lastCallAt && ` · last ${relativeTime(status.data.lastCallAt)}`}
            </p>
          </div>
          <button
            onClick={syncNow}
            disabled={syncing || notConnected}
            title="Sync 3CX now"
            className="rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-xs font-semibold text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] disabled:opacity-50"
          >
            {syncing ? "Syncing…" : "Sync now"}
          </button>
          <button
            onClick={refreshAll}
            title="Refresh"
            aria-label="Refresh calls"
            className="p-1.5 rounded-lg text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))]"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>

        {notConnected && (
          <div className="mx-3 mt-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200">
            <p className="font-semibold flex items-center gap-1.5"><PlugZap className="h-3.5 w-3.5" /> 3CX is not connected</p>
            <p className="mt-0.5">
              {threecx?.status === 'error' && threecx.lastError ? `Last error: ${threecx.lastError}. ` : ''}
              Connect 3CX in Admin → Integrations to ingest calls, transcripts and AI summaries.
            </p>
            <button
              onClick={() => navigate('/admin/integrations')}
              className="mt-2 rounded-lg bg-amber-600 text-white px-2.5 py-1 font-semibold hover:bg-amber-700"
            >
              Open Integrations
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && calls.length === 0 ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}
            </div>
          ) : error ? (
            <EmptyState
              icon="⚠️"
              title="Couldn't load calls"
              description={friendlyError(error)}
              action={<button onClick={refreshAll} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Try again</button>}
            />
          ) : calls.length === 0 ? (
            notConnected ? (
              <EmptyState icon="🔌" title="No calls yet" description="Calls appear here once the 3CX integration is connected in Admin → Integrations." />
            ) : (
              <EmptyState icon="📞" title="No calls ingested yet" description="Calls recorded by 3CX appear here with transcripts, sentiment and an AI draft note." />
            )
          ) : (
            <>
              {calls.map(call => {
                const sentiment = asSentiment(call.sentiment);
                const draftPending = call.draft && (call.draft.status === 'draft' || call.draft.status === 'pending_review');
                return (
                  <button
                    key={call.id}
                    onClick={() => setSelectedId(call.id)}
                    className={cn(
                      'w-full text-left flex items-start gap-3 px-4 py-3.5 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-subtle))] transition-colors',
                      selectedId === call.id && 'bg-brand-50 dark:bg-brand-900/10 border-l-2 border-l-brand-500'
                    )}
                  >
                    <div className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full mt-0.5',
                      sentiment == null ? 'bg-gray-100 dark:bg-gray-800'
                        : sentiment <= 2 ? 'bg-red-100 dark:bg-red-900/30'
                        : sentiment === 3 ? 'bg-gray-100 dark:bg-gray-800'
                        : 'bg-green-100 dark:bg-green-900/30'
                    )}>
                      <Phone className={cn(
                        'h-4 w-4',
                        sentiment == null ? 'text-gray-500' : sentiment <= 2 ? 'text-red-600' : sentiment === 3 ? 'text-gray-500' : 'text-green-600'
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-[hsl(var(--content))] truncate">
                          {call.callerName ?? call.callerNumber}
                        </span>
                        <span className="text-xs text-[hsl(var(--content-placeholder))] shrink-0">{relativeTime(call.startedAt)}</span>
                      </div>
                      <p className="text-xs text-[hsl(var(--content-subtle))] truncate">
                        {call.companyName ?? call.callerNumber}
                        {call.cwTicketId ? ` · #${call.cwTicketId}` : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-[hsl(var(--content-placeholder))]">{formatDuration(call.durationSec ?? 0)}</span>
                        {sentiment && <SentimentChip score={sentiment} />}
                        {draftPending && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">Note pending</span>
                        )}
                        {["queued", "matched", "recording_ready", "transcribing", "retrying"].includes(call.status) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">{call.status.replace(/_/g, " ")}</span>
                        )}
                        {call.status === 'failed' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-medium">Processing failed</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {data?.nextCursor && !atCap && (
                <div className="p-3">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full rounded-lg border border-[hsl(var(--border))] py-2 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] disabled:opacity-50"
                  >
                    {loading ? 'Loading…' : 'Load older calls'}
                  </button>
                </div>
              )}
              {atCap && data?.nextCursor && (
                <p className="px-3 pb-3 text-center text-xs text-[hsl(var(--content-placeholder))]">
                  Showing the most recent {MAX_CALLS} calls.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Call detail */}
      {selectedId ? (
        <CallDetail
          key={selectedId}
          callId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdate={() => { setOlder([]); setCursor(undefined); refetch(); }}
        />
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center">
          <EmptyState icon="📞" title="Select a call" description="Click a call to view transcript, summary, and AI draft note." />
        </div>
      )}
    </div>
  );
}

const SUMMARY_LABELS: Array<[string, string]> = [
  ['issue', 'Issue'],
  ['steps_taken', 'Steps taken'],
  ['resolution', 'Resolution'],
  ['next_steps', 'Next steps'],
];

function summaryText(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : JSON.stringify(x))).join('; ') || null;
  return JSON.stringify(v);
}

function CallDetail({ callId, onClose, onUpdate }: { callId: string; onClose: () => void; onUpdate: () => void }) {
  const { data: call, loading, error, refetch } = useGet<CallDetailDto>(`/api/v1/calls/${callId}`);
  const [draftNote, setDraftNote] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionInfo, setActionInfo] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<'summary' | 'verification' | 'transcript'>('summary');

  React.useEffect(() => {
    setDraftNote(call?.draft?.notes ?? '');
  }, [call?.draft?.id, call?.draft?.notes]);

  async function run(fn: () => Promise<string | void>) {
    setSaving(true);
    setActionError(null);
    setActionInfo(null);
    try {
      const info = await fn();
      if (info) setActionInfo(info);
      refetch();
      onUpdate();
    } catch (e) {
      setActionError(String((e as Error)?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  if (loading && !call) {
    return <div className="flex-1 p-5 space-y-3"><CardSkeleton lines={3} /><CardSkeleton lines={5} /></div>;
  }
  if (error || !call) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState icon="⚠️" title="Couldn't load this call" description={friendlyError(error ?? 'Not found')}
          action={<button onClick={refetch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Try again</button>} />
      </div>
    );
  }

  const draft = call.draft;
  const draftOpen = !!draft && (draft.status === 'draft' || draft.status === 'pending_review');
  const sentiment = asSentiment(call.sentiment ?? call.analysis?.sentiment1To5);
  const turns = call.transcript?.turns ?? [];
  const summaryRows = call.analysis
    ? SUMMARY_LABELS.map(([k, label]) => [label, summaryText(call.analysis!.summary?.[k])] as const).filter(([, v]) => v)
    : [];
  const openAlerts = call.alerts.filter(a => !a.acknowledged);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center gap-4">
        <button onClick={onClose} aria-label="Close call" className="md:hidden text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] transition-colors">
          <X className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-[hsl(var(--content))] truncate">
            {call.callerName ?? call.callerNumber}
          </h2>
          <div className="flex items-center gap-3 text-sm text-[hsl(var(--content-subtle))] flex-wrap">
            {call.companyName && <span>{call.companyName}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDuration(call.durationSec ?? 0)}</span>
            <span>{formatDate(call.startedAt)}</span>
            {call.cwTicketId && <span>Ticket #{call.cwTicketId}</span>}
          </div>
        </div>
        {call.status === "failed" && (
          <button onClick={() => run(() => apiPost(`/api/v1/calls/${call.id}/retry`, {}))} disabled={saving} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">Retry processing</button>
        )}
        {sentiment && <SentimentChip score={sentiment} />}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {call.recordingAvailable && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Call recording</p>
            <audio controls preload="metadata" className="w-full" src={`/api/v1/calls/${call.id}/recording`} />
          </div>
        )}
        {call.statusReason && call.status === "failed" && <p className="text-xs text-red-600">{call.statusReason}</p>}
        {/* Alerts */}
        {openAlerts.map(alert => (
          <div key={alert.id} className="flex items-start gap-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 px-4 py-3">
            <BellRing className="h-4 w-4 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-red-700 dark:text-red-300 capitalize">{alert.kind.replace(/_/g, ' ')} · {alert.severity}</p>
              <p className="text-sm text-red-800 dark:text-red-200 mt-0.5">{alert.message}</p>
            </div>
            <button
              onClick={() => run(() => apiPost(`/api/v1/calls/${call.id}/alerts/${alert.id}/acknowledge`, {}))}
              disabled={saving}
              className="text-xs rounded-lg border border-red-300 dark:border-red-700 px-2 py-1 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              Acknowledge
            </button>
          </div>
        ))}

        {/* AI new-ticket proposal (issue #62) — only for unmatched calls */}
        {!call.cwTicketId && (
          <NewTicketProposalCard callId={call.id} onUpdate={() => refetch()} />
        )}

        {/* AI reason */}
        {call.analysis?.reason && (
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 px-4 py-3">
            <span className="text-lg" aria-hidden="true">🧠</span>
            <div>
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">Sentiment Analysis · {Math.round(call.analysis.confidence * 100)}% confidence</p>
              <p className="text-sm text-blue-800 dark:text-blue-200 mt-0.5">{call.analysis.reason}</p>
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex p-1 gap-1 bg-[hsl(var(--surface-subtle))] rounded-xl w-fit border border-[hsl(var(--border))]">
          {(['summary', 'verification', 'transcript'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize',
                tab === t
                  ? 'bg-[hsl(var(--surface))] text-[hsl(var(--content))] shadow-sm'
                  : 'text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]'
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Summary */}
        {tab === 'summary' && (
          summaryRows.length > 0 ? (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 space-y-2">
              <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">AI Summary</p>
              {summaryRows.map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-medium text-[hsl(var(--content-subtle))]">{label}</p>
                  <p className="text-sm text-[hsl(var(--content))] leading-relaxed">{value}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--content-subtle))]">
              {call.status === 'failed' ? 'Processing failed for this call — no AI summary is available.' : `No AI summary yet (status: ${call.status}).`}
            </p>
          )
        )}

        {/* Verification */}
        {tab === 'verification' && <VerificationPanel callId={call.id} actorBusy={saving} />}

        {/* Transcript */}
        {tab === 'transcript' && (
          turns.length > 0 ? (
            <div className="space-y-3">
              {turns.map((turn, i) => <TranscriptTurn key={i} turn={turn} />)}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--content-subtle))]">No transcript for this call yet.</p>
          )
        )}

        {/* Draft note */}
        {draft && (
          <div className={cn(
            'rounded-xl border p-4',
            draft.status === 'posted'
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10'
              : draft.status === 'rejected'
              ? 'border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]'
              : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10'
          )}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">
                {draft.status === 'posted' ? '✅ Posted to ticket' : draft.status === 'rejected' ? 'Rejected draft note' : draft.status === 'pending_review' ? '🕒 Draft note · awaiting board approval' : '✏️ AI Draft Note'} · #{draft.cwTicketId}
              </p>
              {draftOpen && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  aria-label="Edit draft note"
                  title="Edit"
                  className="text-xs text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] transition-colors"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-2">
                <textarea
                  value={draftNote}
                  onChange={e => setDraftNote(e.target.value)}
                  rows={4}
                  className="w-full text-sm text-[hsl(var(--content))] bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => run(async () => { await apiPatch(`/api/v1/calls/drafts/${draft.id}`, { notes: draftNote }); setEditing(false); })}
                    disabled={saving || !draftNote.trim()}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
                  >
                    {saving ? '…' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setDraftNote(draft.notes); setEditing(false); }}
                    className="px-3 py-1.5 text-xs text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] transition-colors border border-[hsl(var(--border))] rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[hsl(var(--content))] leading-relaxed whitespace-pre-wrap">{draft.notes}</p>
            )}

            {draftOpen && !editing && (
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => run(async () => {
                    const res = await apiPost<{ result: 'posted' | 'drafted' }>(`/api/v1/calls/drafts/${draft.id}/accept`, {});
                    return res.result === 'posted'
                      ? 'Note posted to the ConnectWise ticket.'
                      : 'Board is in shadow mode (or ConnectWise is unavailable): saved as a ticket note draft for review.';
                  })}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-green-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {saving ? 'Saving…' : 'Accept & Post to Ticket'}
                </button>
                <button
                  onClick={() => run(() => apiPost(`/api/v1/calls/drafts/${draft.id}/reject`, {}))}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] disabled:opacity-50"
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            )}
            {actionInfo && <p className="mt-2 text-xs text-green-700 dark:text-green-300">{actionInfo}</p>}
          </div>
        )}
        {actionError && <p className="text-xs text-red-600">{actionError}</p>}
      </div>
    </div>
  );
}

function TranscriptTurn({ turn }: { turn: { speaker: 'tech' | 'client' | 'unknown'; text: string; startSec?: number } }) {
  const isAgent = turn.speaker === 'tech';
  const startSec = Math.floor(turn.startSec ?? 0);
  const timestamp = turn.startSec != null ? `${Math.floor(startSec / 60)}:${String(startSec % 60).padStart(2, '0')}` : '';
  const who = turn.speaker === 'tech' ? 'Tech' : turn.speaker === 'client' ? 'Customer' : 'Speaker';

  return (
    <div className={cn('flex gap-3', isAgent ? 'justify-start' : 'justify-end')}>
      {isAgent && (
        <div className="h-7 w-7 rounded-full bg-brand-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-1">TS</div>
      )}
      <div className={cn(
        'max-w-[75%] rounded-2xl px-4 py-2.5',
        isAgent
          ? 'bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content))]'
          : 'bg-brand-600 text-white'
      )}>
        <p className="text-xs font-semibold mb-0.5 opacity-60">{who}{timestamp && ` · ${timestamp}`}</p>
        <p className="text-sm leading-relaxed">{turn.text}</p>
      </div>
      {!isAgent && (
        <div className="h-7 w-7 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-1">C</div>
      )}
    </div>
  );
}

export function CallsPage() {
  return <CallsWorkspace calls={<CallsPanel />} />;
}
